import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import {
  NotebookText,
  Search,
  SlidersHorizontal,
  BarChart3,
  ShieldAlert,
  Plus,
  Sun,
  Moon,
  LogOut,
  LogIn
} from 'lucide-react';
import LandingPage from './pages/LandingPage';
import NoteDashboard from './pages/NoteDashboard';
import NewNote from './pages/NewNote';
import EditNote from './pages/EditNote';
import AdvancedSearch from './pages/AdvancedSearch';
import Statistics from './pages/Statistics';
import AdminPanel from './pages/AdminPanel';
import logo from './assets/logo.svg';
import { Spinner } from 'reactstrap';
import { publicApi } from './utils/api';
import LanguageSelector from './components/LanguageSelector';

function App() {
  const { keycloak } = useKeycloak();
  const { t, i18n } = useTranslation();
  const [quickSearch, setQuickSearch] = useState('');
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(localStorage.getItem('theme') === 'dark');
  const [languages, setLanguages] = useState([
    { value: 'pl', label: 'PL' }
  ]);

  const isAdmin = keycloak?.hasRealmRole('admin');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await publicApi.get('/countries', {
          params: { onlyActive: true }
        });
        if (response.data && response.data.length > 0) {
          const mappedLanguages = response.data.map(c => ({
            value: c.code,
            label: c.nativeName || c.englishName,
            englishName: c.englishName
          }));
          setLanguages(mappedLanguages);
        }
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    const theme = darkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }, [darkMode]);

  const handleQuickSearch = (e) => {
    e.preventDefault();
    const query = quickSearch.trim();
    if (query) {
      navigate(`/advanced-search?q=${encodeURIComponent(query)}`);
      setQuickSearch('');
    } else {
      navigate('/advanced-search?showAll=true');
    }
  };

  if (!keycloak) return <div className="text-center mt-5"><Spinner color="primary" /></div>;

  return (
    <div className="app-container d-flex flex-column min-vh-100">
      <header className={`navbar sticky-top shadow-sm navbar-custom py-2 ${darkMode ? 'navbar-dark' : 'navbar-light'}`}>
        <div className="container-xxl d-flex flex-wrap flex-md-nowrap align-items-center justify-content-between gap-1 gap-lg-2 px-2 px-md-3">
          {/* Brand + Main Navigation Links */}
          <div className="d-flex align-items-center flex-nowrap gap-1 gap-xl-2 flex-shrink-0 order-1">
            <Link
              className="navbar-brand d-flex align-items-center fw-bold me-1 me-xl-2 py-1 flex-shrink-0"
              to="/"
              title={t('app_title')}
            >
              <img src={logo} alt="Logo" width="30" height="30" className="me-0 me-md-2" />
              <span className="d-none d-md-inline fs-6 fs-xl-5 text-nowrap">{t('app_title')}</span>
            </Link>

            {keycloak.authenticated && (
              <nav className="d-flex align-items-center flex-nowrap gap-1">
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) => `sb-nav-pill ${isActive ? 'active' : ''}`}
                  title={t('your_notes')}
                >
                  <NotebookText size={17} className="flex-shrink-0" />
                  <span className="d-none d-lg-inline">{t('your_notes')}</span>
                </NavLink>

                <NavLink
                  to="/advanced-search"
                  className={({ isActive }) => `sb-nav-pill ${isActive ? 'active' : ''}`}
                  title={t('advanced_search_title')}
                >
                  <SlidersHorizontal size={17} className="flex-shrink-0" />
                  <span className="d-none d-xl-inline">{t('search_button')}</span>
                </NavLink>

                <NavLink
                  to="/statistics"
                  className={({ isActive }) => `sb-nav-pill ${isActive ? 'active' : ''}`}
                  title={t('statistics_title')}
                >
                  <BarChart3 size={17} className="flex-shrink-0" />
                  <span className="d-none d-xl-inline">{t('nav_statistics')}</span>
                </NavLink>

                {isAdmin && (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) => `sb-nav-pill text-warning ${isActive ? 'active' : ''}`}
                    title={t('admin_panel_title')}
                  >
                    <ShieldAlert size={17} className="flex-shrink-0" />
                    <span className="d-none d-xxl-inline">Admin</span>
                  </NavLink>
                )}
              </nav>
            )}
          </div>

          {/* Search Bar: 2nd full-width line on small screens (< md), inline middle element in 1 line on desktop (>= md) */}
          {keycloak.authenticated && (
            <form
              className="sb-top-search sb-header-search order-3 order-md-2 mx-0 mx-md-1 mx-xl-2 mt-1 mt-md-0"
              onSubmit={handleQuickSearch}
            >
              <Search size={16} className="text-muted flex-shrink-0" />
              <input
                type="search"
                placeholder={t('search_placeholder')}
                value={quickSearch}
                onChange={e => setQuickSearch(e.target.value)}
                aria-label={t('search_placeholder')}
              />
              <button
                className="btn btn-sm btn-primary rounded-pill px-3 px-md-2 px-xl-3 py-1 d-inline-flex align-items-center justify-content-center gap-1 fw-semibold flex-shrink-0"
                type="submit"
                title={t('search_button')}
              >
                <Search size={14} className="d-none d-md-inline d-xl-none" />
                <span className="d-inline d-md-none d-xl-inline small">{t('search_button')}</span>
              </button>
            </form>
          )}

          {/* Action Buttons: New Note, Theme, Language, Auth */}
          <div className="d-flex align-items-center flex-nowrap gap-1 gap-sm-2 flex-shrink-0 order-2 order-md-3">
            {keycloak.authenticated && (
              <Link
                to="/new-note"
                className="btn btn-primary btn-sm text-nowrap rounded-pill px-2 px-lg-3 py-1 shadow-sm fw-bold d-inline-flex align-items-center justify-content-center gap-1"
                style={{ minHeight: '34px', minWidth: '34px' }}
                title={t('new_note_title')}
              >
                <Plus size={17} className="flex-shrink-0" />
                <span className="d-none d-lg-inline">{t('new_note_title')}</span>
              </Link>
            )}

            <button
              className={`btn btn-sm rounded-circle p-0 shadow-sm border d-flex align-items-center justify-content-center flex-shrink-0 ${darkMode ? 'btn-outline-light border-secondary' : 'btn-light border-secondary-subtle'}`}
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? t('light_mode') : t('dark_mode')}
              style={{ width: '34px', height: '34px' }}
            >
              {darkMode ? <Sun size={17} className="text-warning" /> : <Moon size={17} />}
            </button>

            <LanguageSelector languages={languages} darkMode={darkMode} />

            {!keycloak.authenticated ? (
              <button
                className="btn btn-primary btn-sm rounded-pill px-2 px-sm-3 py-1 shadow-sm fw-bold d-inline-flex align-items-center justify-content-center gap-1 flex-shrink-0"
                style={{ minHeight: '34px' }}
                onClick={() => keycloak.login()}
                title={t('login')}
              >
                <LogIn size={16} className="flex-shrink-0" />
                <span className="d-none d-sm-inline">{t('login')}</span>
              </button>
            ) : (
              <button
                className="btn btn-outline-danger btn-sm rounded-pill px-2 px-xl-3 py-1 text-nowrap fw-semibold d-inline-flex align-items-center justify-content-center gap-1 flex-shrink-0"
                style={{ minHeight: '34px', minWidth: '34px' }}
                onClick={() => keycloak.logout()}
                title={t('logout')}
              >
                <LogOut size={16} className="flex-shrink-0" />
                <span className="d-none d-xl-inline">{t('logout')}</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="py-0 flex-grow-1">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dashboard" element={keycloak.authenticated ? <NoteDashboard /> : <LandingPage />} />
          <Route path="/new-note" element={keycloak.authenticated ? <NewNote /> : <LandingPage />} />
          <Route path="/share-target" element={keycloak.authenticated ? <NewNote /> : <LandingPage />} />
          <Route path="/edit/:id" element={keycloak.authenticated ? <EditNote /> : <LandingPage />} />
          <Route path="/advanced-search" element={keycloak.authenticated ? <AdvancedSearch /> : <LandingPage />} />
          <Route path="/statistics" element={keycloak.authenticated ? <Statistics /> : <LandingPage />} />
          <Route path="/admin" element={keycloak.authenticated && isAdmin ? <AdminPanel /> : <LandingPage />} />
        </Routes>
      </main>

      <footer className="bg-light bg-opacity-50 text-center py-4 mt-5 border-top">
        <div className="container">
          <p className="text-muted small mb-1">{t('footer_text')}</p>
          <p className="text-muted mb-0" style={{ fontSize: '0.7rem' }}>
            {t('trademark_notice')}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
